import { AppLanguage, getLanguageStrings } from "internationalisation";
import { assertNever } from "../helper";
import { RespondentConfigBuilder } from "../respondentConfigBuilder";
import { generateUploadsTask, generateWebPlatformTask, generateWebRequestTask } from "../tasks";
import { WebPlatform } from "../types";

export const HSAL0226CollGroupIds = [
	"HSAL-465-0226-UK-FACEBOOK",
	"HSAL-466-0226-UK-INSTAGRAM",
	"HSAL-467-0226-UK-TIKTOK",
	"HSAL-468-0226-UK-SNAP",
	"HSAL-469-0226-UK-X"
] as const;

export type HSAL0226CollGroupId = typeof HSAL0226CollGroupIds[number];

type HSAL0226CollConfig = {
	groupId: HSAL0226CollGroupId,
	metaFieldNames: ReadonlyArray<string>
}

export const HSAL0226CollWebPlatformIds = [
	"HSAL-0226-UK-FACEBOOK",
	"HSAL-0226-UK-INSTAGRAM",
	"HSAL-0226-UK-TIKTOK",
	"HSAL-0226-UK-SNAP",
	"HSAL-0226-UK-X"
] as const;

export type HSAL0226CollWebPlatformId = typeof HSAL0226CollWebPlatformIds[number];

const getWPTaskLengthMinutes = (webPlatformId: HSAL0226CollWebPlatformId): string => {
	switch (webPlatformId) {
		case "HSAL-0226-UK-FACEBOOK":
		case "HSAL-0226-UK-INSTAGRAM":
		case "HSAL-0226-UK-X":
		case "HSAL-0226-UK-SNAP":
			return "7";

		case "HSAL-0226-UK-TIKTOK":
			return "10";
		
		default:
			assertNever(webPlatformId);
	}
}

const getWebPlatformConfigUrl = (webPlatformId: HSAL0226CollWebPlatformId): string => {
	return `https://atrace-cdn.amplifiedintelligence.io/WebPlatform/Configs/HSAL-0226/${webPlatformId}.json`
}

const getWebPlatform = (webPlatformId: HSAL0226CollWebPlatformId): WebPlatform => {
	switch (webPlatformId) {
		case "HSAL-0226-UK-FACEBOOK": return "Facebook";
		case "HSAL-0226-UK-INSTAGRAM": return "Instagram";
		case "HSAL-0226-UK-TIKTOK": return "TikTok";
		case "HSAL-0226-UK-SNAP": return "Snapchat";
		case "HSAL-0226-UK-X": return "X";

		default: assertNever(webPlatformId);
	}
}

const getWebPlatformId = (groupId: HSAL0226CollGroupId): HSAL0226CollWebPlatformId => {
	switch (groupId) {
		case "HSAL-465-0226-UK-FACEBOOK": return "HSAL-0226-UK-FACEBOOK";
		case "HSAL-466-0226-UK-INSTAGRAM": return "HSAL-0226-UK-INSTAGRAM";
		case "HSAL-467-0226-UK-TIKTOK": return "HSAL-0226-UK-TIKTOK";
		case "HSAL-468-0226-UK-SNAP": return "HSAL-0226-UK-SNAP";
		case "HSAL-469-0226-UK-X": return "HSAL-0226-UK-X";

		default: assertNever(groupId);
	}
}

const getWebRequestUrl = (groupId: HSAL0226CollGroupId): string => {
	let surveyId: string;
	switch (groupId) {
		case "HSAL-465-0226-UK-FACEBOOK":
			surveyId = "SV_9o7ikQqNI7iUTau";
			break;
		case "HSAL-466-0226-UK-INSTAGRAM":
			surveyId = "SV_aV1hvy0mqHnS3vU";
			break;
		case "HSAL-467-0226-UK-TIKTOK":
			surveyId = "SV_0dNz3YcZf4gVBWe";
			break;
		case "HSAL-468-0226-UK-SNAP":
			surveyId = "SV_9X2dFcgDk8k25EO";
			break;
		case "HSAL-469-0226-UK-X":
			surveyId = "SV_bjPDl0ipwhzdEN0";
			break;
		
		default:
			assertNever(groupId);
	}

	return `https://survey.au1.qualtrics.com/jfe/form/${surveyId}`;
}

export const addHSAL0226CollConfig = (
	config: HSAL0226CollConfig,
	builder: RespondentConfigBuilder
): RespondentConfigBuilder => {
	const FINISH_MESSAGE = "Scroll complete. Reward unlocked. Nicely done 👏\nThat’s everything! Your session has been recorded and your reward is on its way. Thanks for taking part, it really is as easy as that to earn with Blinq!";

	const webPlatformId = getWebPlatformId(config.groupId);
	const webPlatformTask = generateWebPlatformTask({
		groupId: config.groupId,
		platform: getWebPlatform(webPlatformId),
		webPlatformConfigUrl: getWebPlatformConfigUrl(webPlatformId),
		webPlatformID: webPlatformId,
		langStrings: getLanguageStrings(AppLanguage.en),
		taskLengthMinutes: getWPTaskLengthMinutes(webPlatformId)
	});
	
	const webRequestTask = generateWebRequestTask({
        webRequestConfigUrl: `https://atrace-cdn.amplifiedintelligence.io/WebRequestConfigs/HSAL-0226/${config.groupId}_INCENTIVE.json`,
		params: {},
		taskID: config.groupId + '-WEBREQUEST'
	});

	const uploadTask = generateUploadsTask({
		config: {
			"title": "Uploads"
		},
		taskId: config.groupId + '-UPLOADS'
	});

	builder
		.setFlag('deviceLock', 'mobile')
		.setFlag('requireMinimumBattery', 0.3)
		.setFlag('preventTaskProgress', false)
		.setFlag('finishMessageOverride', FINISH_MESSAGE)
		.addTask(webPlatformTask)
		.addTask(webRequestTask)
		.addTask(uploadTask);

	return builder;
}